/**
 * Coercion helpers that normalize API payloads into safe UI values.
 */
import PropTypes from "prop-types";

/**
 * Returns the value when it is an array; otherwise an empty array.
 *
 * @param {*} value Candidate value.
 * @returns {Array}
 */
export const asArray = (value) => (Array.isArray(value) ? value : []);

/**
 * Returns the value when it is a non-array object; otherwise an empty object.
 *
 * @param {*} value Candidate value.
 * @returns {Object}
 */
export const asObject = (value) => (value && typeof value === "object" && !Array.isArray(value) ? value : {});

/**
 * Coerces a value to a string, using the fallback when the value is nullish.
 *
 * @param {*} value Candidate value.
 * @param {string} [fallback=""] Value used when `value` is null or undefined.
 * @returns {string}
 */
export const asString = (value, fallback = "") => (value == null ? fallback : typeof value === "string" ? value : String(value));

/**
 * Coerces a value to a finite number, using the fallback when conversion fails.
 *
 * @param {*} value Candidate value.
 * @param {number} [fallback=0] Value used when `value` is not a finite number.
 * @returns {number}
 */
export const asNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

/**
 * Coerces a value to a boolean, using the fallback when the value is nullish.
 *
 * @param {*} value Candidate value.
 * @param {boolean} [fallback=false] Value used when `value` is null or undefined.
 * @returns {boolean}
 */
export const asBool = (value, fallback = false) => (typeof value === "boolean" ? value : value == null ? fallback : Boolean(value));

/**
 * Returns the value when it is a function; otherwise the fallback callback.
 *
 * @param {*} value Candidate value.
 * @param {Function} [fallback] Callback used when `value` is not a function.
 * @returns {Function}
 */
export const asFunc = (value, fallback = () => {}) => (typeof value === "function" ? value : fallback);

export const PT = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  flag: PropTypes.oneOfType([PropTypes.bool, PropTypes.number, PropTypes.string]),
  pageList: PropTypes.shape({
    total: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    page: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    page_size: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    data: PropTypes.array,
  }),
  user: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    username: PropTypes.string,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    avatar: PropTypes.string,
    phone_number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    role: PropTypes.string,
    location: PropTypes.string,
  }),
};

/**
 * Normalizes a paginated list payload, wrapping a raw array when needed.
 *
 * @param {Object|Array} value List payload or raw array.
 * @param {number} [fallbackPageSize=10] Page size used when none is provided.
 * @returns {{ total: number, page: number, page_size: number, data: Array }}
 */
export const asList = (value, fallbackPageSize = 10) => {
  if (Array.isArray(value)) {
    return { total: value.length, page: 0, page_size: fallbackPageSize, data: value };
  }
  const data = asArray(value?.data);
  const page = Number(value?.page) || 0;
  const page_size = Number(value?.page_size) || fallbackPageSize;
  const list = {
    total: Number(value?.total) || data.length,
    page,
    page_size,
    data,
  };
  // Inspect payloads carry course-level totals outside `data`.
  if (value && typeof value === "object") {
    if (value.total_spent != null) list.total_spent = Number(value.total_spent) || 0;
    if (value.quiz_spent != null) list.quiz_spent = Number(value.quiz_spent) || 0;
  }
  return list;
};

/**
 * Parses a JSON string, returning objects unchanged and falling back on failure.
 *
 * @param {*} value JSON string, object, or empty value.
 * @param {*} [fallback={}] Value returned when parsing fails.
 * @returns {*}
 */
export const safeParse = (value, fallback = {}) => {
  if (value == null || value === "") return fallback;
  if (typeof value === "object") return value;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

/**
 * Looks up a named theme token and returns its value or the fallback.
 *
 * @param {Array} themes Theme token list.
 * @param {string} name Token name.
 * @param {string} [fallback=""] Value used when the token is missing.
 * @returns {string}
 */
export const themeValue = (themes, name, fallback = "") => asArray(themes).find((v) => v?.name === name)?.value || fallback;
